// frontend/src/hooks/useUnsavedGuard.tsx
//
// BUG-2026-0189 (rework of #576) — block IN-APP navigation away from a test
// workflow page while there are unsaved edits.
//
// #576 added a browser-level `beforeunload` warning (refresh / close / URL
// typing) via useUnsavedChangesWarning. That does NOT cover SPA navigation —
// clicking the top nav, the logo, a breadcrumb, "Back to Work Order", or the
// browser Back button all change the route WITHOUT a page unload, so the
// native prompt never fires and edits were silently lost.
//
// This app uses <BrowserRouter> (App.tsx), NOT a data router, so react-router
// v7's `useBlocker` is unavailable without migrating every route. Instead we
// use a small context guard:
//
//   • The active workflow page registers a `LeaveGuard` ({ isDirty, confirmLeave })
//     while it is mounted. `confirmLeave(proceed)` opens the page's own
//     Save & Leave / Discard & Leave / Cancel dialog and only runs `proceed`
//     on the user's choice.
//   • Every in-app exit surface (nav links, logo, Directory menu, search,
//     breadcrumbs, mobile nav, the "Back to Work Order" button) routes its
//     navigation through `useGuardedNavigate()` / <GuardedLink>, which asks the
//     registered guard first.
//   • The browser Back button is intercepted here centrally: while a dirty
//     guard is registered we keep a sentinel history entry so the first Back is
//     absorbed and turned into the same dialog.
//
// One workflow page is mounted at a time, so a single active guard suffices.

import {
  createContext, useCallback, useContext, useEffect, useRef, useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import type { ConfirmDialogState } from '../components/shared/ConfirmDialog';

export interface LeaveGuard {
  /** True while the page has unsaved edits. */
  isDirty: () => boolean;
  /**
   * Open the page's Save & Leave / Discard & Leave / Cancel dialog. `proceed`
   * runs only if the user chooses to leave (after a successful save, or on
   * discard). If the user cancels, nothing happens.
   */
  confirmLeave: (proceed: () => void) => void;
}

interface UnsavedGuardContextValue {
  registerGuard: (g: LeaveGuard) => () => void;
  /** Run `proceed` immediately if clean, else route it through the guard dialog. */
  runGuarded: (proceed: () => void) => void;
}

const UnsavedGuardContext = createContext<UnsavedGuardContextValue | null>(null);

/**
 * Build the shared "Unsaved Changes" dialog state. ONE implementation used by
 * every workflow page's "Back to Work Order" button AND by the nav / back-button
 * guard, so the copy and button semantics never drift (DRY, CODING-STANDARDS).
 *
 * `save` must resolve `true` only when the save actually persisted; on `false`
 * (validation/permission/conflict) we keep the user on the page with their
 * edits intact rather than navigating away.
 */
export function buildUnsavedLeaveDialog(opts: {
  save: () => Promise<boolean>;
  proceed: () => void;
}): ConfirmDialogState {
  return {
    open: true,
    title: 'Unsaved Changes',
    message: 'You have unsaved changes. Save them before leaving, or discard your changes and leave?',
    confirmLabel: 'Save & Leave',
    confirmColor: 'primary',
    onConfirm: () => {
      void (async () => {
        if (await opts.save()) opts.proceed();
      })();
    },
    secondaryLabel: 'Discard & Leave',
    secondaryColor: 'warning',
    onSecondary: opts.proceed,
  };
}

export function UnsavedGuardProvider({ children }: { children: ReactNode }) {
  // A SET of guards, not a single slot: TestWorkflowPage registers its own
  // (Direct Shear) guard AND delegates to a child workflow page that registers
  // its own. Only one is ever dirty at a time, so we always act on whichever
  // guard currently reports unsaved edits — never a stale/clean overwrite.
  const guardsRef = useRef<Set<LeaveGuard>>(new Set());
  const [hasDirtyGuard, setHasDirtyGuard] = useState(false);

  const dirtyGuard = useCallback((): LeaveGuard | null => {
    for (const g of guardsRef.current) if (g.isDirty()) return g;
    return null;
  }, []);

  const recomputeDirty = useCallback(() => {
    setHasDirtyGuard(dirtyGuard() !== null);
  }, [dirtyGuard]);

  const registerGuard = useCallback((g: LeaveGuard) => {
    guardsRef.current.add(g);
    setHasDirtyGuard(dirtyGuard() !== null);
    return () => {
      guardsRef.current.delete(g);
      setHasDirtyGuard(dirtyGuard() !== null);
    };
  }, [dirtyGuard]);

  const runGuarded = useCallback((proceed: () => void) => {
    const g = dirtyGuard();
    if (g) g.confirmLeave(proceed);
    else proceed();
  }, [dirtyGuard]);

  // ── Browser Back interception ──────────────────────────────────────────
  // While a dirty guard is registered, keep one sentinel history entry so the
  // first Back press is absorbed (popstate) and converted into the leave
  // dialog instead of navigating away.
  useEffect(() => {
    if (!hasDirtyGuard) return;
    let bypass = false;
    // Poll the guard so the sentinel is torn down promptly once the page is
    // saved/cleaned (dirty flips false without re-registering).
    const poll = window.setInterval(recomputeDirty, 400);

    window.history.pushState({ __unsavedGuardSentinel: true }, '');

    const onPop = () => {
      if (bypass) { bypass = false; return; }
      const g = dirtyGuard();
      if (g) {
        // Re-arm the sentinel so we stay put; page URL is unchanged so React
        // does not remount. Then ask the user.
        window.history.pushState({ __unsavedGuardSentinel: true }, '');
        g.confirmLeave(() => {
          bypass = true;
          // Skip past the sentinel AND the current page to the prior entry.
          window.history.go(-2);
        });
      } else {
        // No longer dirty — let the Back through.
        bypass = true;
        window.history.back();
      }
    };

    window.addEventListener('popstate', onPop);
    return () => {
      window.clearInterval(poll);
      window.removeEventListener('popstate', onPop);
    };
  }, [hasDirtyGuard, recomputeDirty, dirtyGuard]);

  return (
    <UnsavedGuardContext.Provider value={{ registerGuard, runGuarded }}>
      {children}
    </UnsavedGuardContext.Provider>
  );
}

function useUnsavedGuardContext(): UnsavedGuardContextValue | null {
  return useContext(UnsavedGuardContext);
}

/**
 * Returns a navigate function that first asks the registered unsaved-changes
 * guard (if any). Safe to use on ANY page — when no workflow page has
 * registered a dirty guard it navigates immediately, exactly like useNavigate.
 */
export function useGuardedNavigate(): (to: string) => void {
  const navigate = useNavigate();
  const ctx = useUnsavedGuardContext();
  return useCallback(
    (to: string) => {
      if (ctx) ctx.runGuarded(() => navigate(to));
      else navigate(to);
    },
    [ctx, navigate],
  );
}

/**
 * Registered by the active test workflow page. Wires the page's dirty flag and
 * save path into the shared guard, and returns `requestLeave(proceed)` for the
 * page's own "Back to Work Order" button to reuse the SAME dialog.
 *
 * @param dirty   current unsaved-edits flag
 * @param save    the page's existing save path; must resolve true only when the
 *                save actually persisted (false = stay put, keep edits)
 * @param setConfirm the page's ConfirmDialog state setter (reuses the dialog
 *                already rendered on every workflow page)
 */
export function useRegisterUnsavedGuard(params: {
  dirty: boolean;
  save: () => Promise<boolean>;
  setConfirm: (s: ConfirmDialogState) => void;
}): (proceed: () => void) => void {
  const { dirty, save, setConfirm } = params;
  const ctx = useUnsavedGuardContext();

  // Keep the latest closures in refs so the registered guard object is stable
  // but always sees current dirty/save/setConfirm.
  const dirtyRef = useRef(dirty);
  const saveRef = useRef(save);
  const setConfirmRef = useRef(setConfirm);
  dirtyRef.current = dirty;
  saveRef.current = save;
  setConfirmRef.current = setConfirm;

  const confirmLeave = useCallback((proceed: () => void) => {
    setConfirmRef.current(
      buildUnsavedLeaveDialog({ save: () => saveRef.current(), proceed }),
    );
  }, []);

  // Register (and re-register when dirty flips, refreshing the provider's
  // back-button sentinel arming).
  useEffect(() => {
    if (!ctx) return;
    return ctx.registerGuard({
      isDirty: () => dirtyRef.current,
      confirmLeave,
    });
  }, [ctx, dirty, confirmLeave]);

  return useCallback(
    (proceed: () => void) => {
      if (dirtyRef.current) confirmLeave(proceed);
      else proceed();
    },
    [confirmLeave],
  );
}
